import type { Branch } from "~/branching/model/branch";
import { PROTECTED_BRANCH_REASON } from "~/branching/model/protected-branch";

export interface ChatSaveDestination {
  systemId: string;
  systemName: string;
  branch: Branch;
}

// What the user picked on a save form. The destination itself is resolved by the feature
// layer from the page the chat is open on; the form only says whether to use it.
export interface ChatSaveChoices {
  infosecLevelId?: string;
  controlTagIds?: string[];
  saveToSystem?: boolean;
}

// The resource always exists once a save resolves; these say which follow-up steps stuck.
// `placedInSystem` is null when no system placement was asked for; `placementRefusal` names a
// failed placement the user can't fix from the system directory.
export interface ChatSaveOutcome {
  infosecApplied: boolean;
  controlTagsApplied: boolean;
  placedInSystem: boolean | null;
  placementRefusal?: "protected-branch";
}

export function describeSaveDestination(destination: Pick<ChatSaveDestination, "systemName" | "branch">): string {
  return `${destination.systemName} · ${destination.branch.name}`;
}

export function chatSaveWarnings(outcome: ChatSaveOutcome, systemName?: string): string[] {
  const warnings: string[] = [];
  if (!outcome.infosecApplied) warnings.push("The infosec level could not be applied. Set it from the resource page.");
  if (!outcome.controlTagsApplied) {
    warnings.push("The control tags could not be applied. Set them from the resource page.");
  }
  if (outcome.placedInSystem === false) {
    const remedy =
      outcome.placementRefusal === "protected-branch" ? PROTECTED_BRANCH_REASON : "Add it from the system directory.";
    warnings.push(`It could not be added to ${systemName ?? "the system"}. ${remedy}`);
  }
  return warnings;
}
